/**
 * bounty_logic.ts — PURE bounty-hunter math (2026-08-30): contract tiers,
 * the WEAKNESS algorithm (the director's strike decision), and the stalking
 * ring geometry. BYTE-MIRRORED in server/src/lib/bounty_logic.ts (the
 * town_layout mirror law) — the harness locksteps them.
 *
 * Design research (Alien: Isolation / RE2 Mr. X / Elite / EVE / Nemesis):
 *  - director/puppet split: the server owns the contract + hp; the client
 *    choreographs the body. The weakness score is the director's patience.
 *  - menace pacing: stalking holds a MEDIUM dread — sightings at a distance,
 *    retreat when approached; the strike needs a SUSTAINED opening.
 *  - spend is a SINK debited from the hirer; salvage on a slain hunter is a
 *    fraction of the spend (bounty-farming must never print money — EVE law).
 */

// ── Contract tiers — the more credits spent, the tougher the hunter ──────
export interface BountyTier {
  tier: number;
  name: string;          // the hunter archetype (flavor + plate)
  minSpend: number;      // coins (closed loop) or SOV (player-placed)
  hp: number;            // ground hull; space hull = hp * 1.6
  damage: number;        // per landed strike on the ground
  strikeThreshold: number; // weakness score needed to attack (lower = bolder)
  stalkHoldS: number;    // seconds the score must HOLD above threshold
  shadowRadiusM: number; // preferred stalking ring distance
}

export const BOUNTY_TIERS: BountyTier[] = [
  { tier: 1, name: 'Hired Blade', minSpend: 400, hp: 140, damage: 11, strikeThreshold: 72, stalkHoldS: 10, shadowRadiusM: 120 },
  { tier: 2, name: 'Tracker', minSpend: 1500, hp: 260, damage: 16, strikeThreshold: 62, stalkHoldS: 8, shadowRadiusM: 100 },
  { tier: 3, name: 'Revenant', minSpend: 5000, hp: 430, damage: 22, strikeThreshold: 50, stalkHoldS: 6, shadowRadiusM: 85 },
  { tier: 4, name: 'Wraith of the Ledger', minSpend: 15000, hp: 650, damage: 30, strikeThreshold: 38, stalkHoldS: 4, shadowRadiusM: 70 },
];

/** The tier a spend buys (the floor tier for the minimum spend). */
export function tierForSpend(spend: number): BountyTier {
  let best = BOUNTY_TIERS[0];
  for (const t of BOUNTY_TIERS) if (spend >= t.minSpend) best = t;
  return best;
}

/** Salvage a slain hunter drops — deliberately a FRACTION of the spend. */
export function salvageForSpend(spend: number): number {
  return Math.max(25, Math.floor(spend * 0.1));
}

// ── The WEAKNESS algorithm — many metrics, one patient decision ──────────
export interface WeaknessMetrics {
  /** 0..1 — player hp fraction (ground) or hull fraction (space). */
  healthFrac: number;
  /** metres to the nearest settlement center (ground) / station (space). */
  distToSafetyM: number;
  /** other PLAYERS within 200 m — witnesses. */
  playersNearby: number;
  /** true when the local world clock reads night. */
  night: boolean;
  /** true when the player took damage in the last 60 s (already bleeding). */
  recentlyHurt: boolean;
  /** true when already fighting something else (creatures, pirates, war). */
  inOtherCombat: boolean;
  /** true on foot (ground) — a mounted/flying player is harder prey. */
  onFoot: boolean;
  /** m/s — a stationary player (crafting, trading, reading) is exposed. */
  speedMps: number;
  /** true when no weapon is equipped. */
  unarmed: boolean;
  /** true in deep space, far from any structure (space domain). */
  deepSpace: boolean;
}

/**
 * The weakness score 0..100. Weights sum to 100; health dominates but no
 * single metric can trigger a strike alone — the hunter waits for a
 * CONFLUENCE (that patience is the creepy part).
 */
export function weaknessScore(m: WeaknessMetrics): number {
  let s = 0;
  s += (1 - Math.max(0, Math.min(1, m.healthFrac))) * 34;          // bleeding
  s += Math.max(0, Math.min(1, m.distToSafetyM / 800)) * 16;       // isolated
  s += (m.playersNearby <= 0 ? 12 : m.playersNearby === 1 ? 5 : 0); // unwatched
  s += m.night ? 8 : 0;                                             // dark
  s += m.recentlyHurt ? 7 : 0;                                      // wounded
  s += m.inOtherCombat ? 7 : 0;                                     // distracted
  s += m.onFoot ? 5 : 0;                                            // grounded
  s += m.speedMps < 0.5 ? 5 : m.speedMps < 2 ? 2 : 0;               // exposed
  s += m.unarmed ? 4 : 0;                                           // defenseless
  s += m.deepSpace ? 2 : 0;                                         // beyond help
  return Math.min(100, Math.round(s));
}

/**
 * Strike hysteresis: feed the score once a second; the strike arms only
 * after `stalkHoldS` CONSECUTIVE seconds at/above the tier threshold.
 * Returns the new hold counter; the caller strikes when it reaches
 * `stalkHoldS` (a single dip resets the patience — the opening must be real).
 */
export function strikeHold(prevHoldS: number, score: number, t: BountyTier): number {
  return score >= t.strikeThreshold ? prevHoldS + 1 : 0;
}

// ── Stalking geometry — the ring, the retreat, the sightline ─────────────
/**
 * Where the shadowing hunter wants to stand: on the ring at `shadowRadiusM`
 * behind the player's facing (rear arc ±55°), drifting with a slow orbit so
 * repeated glances find it in a DIFFERENT place. Pure — jitter comes from
 * the caller's seeded hash, never Math.random.
 */
export function shadowPos(
  px: number, pz: number, playerYaw: number, t: BountyTier,
  orbitPhase: number, jitter01: number,
): { x: number; z: number } {
  const rear = playerYaw + Math.PI; // behind the player
  const arc = rear + Math.sin(orbitPhase) * 0.96 + (jitter01 - 0.5) * 0.5;
  const r = t.shadowRadiusM * (0.85 + jitter01 * 0.3);
  return { x: px + Math.sin(arc) * r, z: pz + Math.cos(arc) * r };
}

/** Approach inside this range while stalking → the hunter breaks for its ship. */
export const SHADOW_FLEE_M = 38;

// ── The escape (2026-09-07: "instead of having him disappear have him go into
// his ship and fly away") ─────────────────────────────────────────────────
/** The ship sets down this far beyond the hunter, on the line away from you. */
export const ESCAPE_SHIP_M = 26;
/** The hunter is aboard once this close to the hatch. */
export const ESCAPE_BOARD_M = 2.2;
/** Boarding pause before the lift (s). */
export const ESCAPE_BOARD_S = 0.8;
/** The climb-out lasts this long, then the hull is gone (s). */
export const ESCAPE_FLIGHT_S = 9;
/** Its run to the hatch is faster than its stalking pace (× CHASE_SPEED). */
export const ESCAPE_RUN_MULT = 1.25;

/**
 * Where the escape ship lands and how it leaves: on the ray from the player
 * THROUGH the hunter, ESCAPE_SHIP_M past the hunter (never between you and it,
 * never on you), hatch on the hunter's side, nose pointing away — so the run,
 * the lift and the flight all read as one motion AWAY from you. Pure.
 */
export function escapeShipSpot(
  px: number, pz: number, hx: number, hz: number,
): { x: number; z: number; yaw: number; hatchX: number; hatchZ: number; awayX: number; awayZ: number } {
  let ax = hx - px, az = hz - pz;
  const d = Math.hypot(ax, az);
  if (d < 0.01) { ax = 0; az = 1; } else { ax /= d; az /= d; }
  const x = hx + ax * ESCAPE_SHIP_M, z = hz + az * ESCAPE_SHIP_M;
  return {
    x, z,
    yaw: Math.atan2(ax, az), // three.js: rotation.y facing (sin, cos) → +Z forward turned to the away vector
    hatchX: x - ax * 4.5, hatchZ: z - az * 4.5,
    awayX: ax, awayZ: az,
  };
}

/** The climb-out at t seconds: rise speed and forward speed ramp (m/s). */
export function escapeFlight(t: number): { riseMps: number; forwardMps: number; pitch: number; bank: number } {
  const u = Math.max(0, Math.min(1, t / ESCAPE_FLIGHT_S));
  return {
    riseMps: Math.min(28, 6 + t * 14),
    forwardMps: Math.min(95, t * t * 9),
    pitch: -0.18 * Math.min(1, t / 2),          // nose up as the thrust comes on
    bank: Math.sin(t * 1.4) * 0.12 * (1 - u * 0.5),
  };
}
/** The hunter re-materializes no closer than this after a flee. */
export const SHADOW_REAPPEAR_MIN_M = 150;

// ── Hire gates (mirrored constants — the server enforces) ────────────────
/** A versona hires only against its NEMESIS (the brand itself is the gate —
 *  promotion already requires net ≤ −60 plus a PERMANENT harm). */
export const HIRE_RELATION_NET_MAX = -60;
/** A settlement hires only against regard ≤ this. MUST sit BELOW
 *  REGARD_START (10) — a fresh player has never wronged anyone; only an
 *  EARNED hatred (piracy −80 floor, crimes) reaches the hire gate. */
export const HIRE_STANDING_MAX = 5;
/** A civilization hires when ≥ this many of its settlements hate the mark. */
export const HIRE_CIV_TOWNS_MIN = 3;
/** Cooldown between contracts on the same target from the same contractor. */
export const HIRE_COOLDOWN_S = 6 * 3600;
/** The stalk must AGE this long before any strike is valid (the dread floor). */
export const STALK_MIN_S = 120;

/** Deterministic hunter name — personal, Nemesis-style (FNV-1a + xorshift). */
export function hunterName(seed: number): string {
  const first = ['Vesk', 'Maro', 'Sable', 'Odran', 'Kilva', 'Thrane', 'Ismet', 'Corvo', 'Ryn', 'Haldis'];
  const epithet = ['the Quiet', 'Six-Fingers', 'of the Long Watch', 'the Debt-Keeper', 'Coldeye', 'the Patient', 'Ash-Walker', 'the Collector', 'Hollowstep', 'Last-Light'];
  let h = (seed ^ 0x811c9dc5) >>> 0;
  h ^= h << 13; h >>>= 0; h ^= h >> 17; h ^= h << 5; h >>>= 0;
  return `${first[h % first.length]} ${epithet[(h >>> 8) % epithet.length]}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// THE STALKING REWORK (2026-09-18) — phases, the opening, evidence, cues.
// Everything below is PURE and mirrored (the harness locksteps it). The
// server's hunt tick (lib/bounty_hunt.ts) is the only writer of the numbers;
// the client reads them to render and to PREDICT (never to decide).
// ═══════════════════════════════════════════════════════════════════════════

/** The encounter phase — orthogonal to the contract's state. `attacking` now
 *  means HOSTILITY ESTABLISHED; whether the hunter is closing, searching or
 *  gone is the phase. A professional can stop chasing this minute and still
 *  be hired tomorrow: persistence belongs to the contract, not to aggression. */
export type HuntPhase =
  | 'track'      // not materialized: waiting for the next opportunity, following the coarse lead
  | 'observe'    // standing at a chosen vantage, verifying the target
  | 'shadow'     // moving between vantages, keeping the tier band, laying cues
  | 'stage'      // the opening is building: moving to an ambush position
  | 'commit'     // the readable approach tell (weapon readiness) before the first blow
  | 'engage'     // ordinary authoritative combat (npc_strike windups)
  | 'break'      // contact broken (by either side): one tick of bookkeeping
  | 'search'     // checking plausible places from stale knowledge — can fail
  | 'withdraw'   // gone (the ship escape when it was seen leaving); recovery runs
  | 'suspended'; // target offline / sanctuary / unsupported domain
export const HUNT_PHASES: HuntPhase[] = ['track', 'observe', 'shadow', 'stage', 'commit', 'engage', 'break', 'search', 'withdraw', 'suspended'];
/** Phases in which a body exists in the world for the target to find. */
export const MATERIAL_PHASES: HuntPhase[] = ['observe', 'shadow', 'stage', 'commit', 'engage', 'search'];

// ── The opening: enter/exit hysteresis with a decaying dwell ──────────────
export interface Opening { armed: boolean; heldS: number }
export interface HoldRules { enter: number; exit: number; requiredS: number; decayPerS: number }
/** The exit threshold sits this far under the tier's enter threshold. */
export const HOLD_EXIT_BAND = 8;
/** Dwell lost per second while the score sits in the hysteresis band. */
export const HOLD_DECAY_PER_S = 0.5;
/** A tick gap longer than this is a stalled simulation: revalidate from zero.
 *  The hunt tick is 2 Hz; the sim tick blocks the module ~1.2 s at a time, so the
 *  stall line sits at 3 s (measured 2026-09-18) — a wedged scheduler still resets. */
export const OPENING_STALL_S = 3;

export function holdRulesFor(t: BountyTier): HoldRules {
  return { enter: t.strikeThreshold, exit: t.strikeThreshold - HOLD_EXIT_BAND, requiredS: t.stalkHoldS, decayPerS: HOLD_DECAY_PER_S };
}
export function validHoldRules(r: HoldRules): boolean {
  return Number.isFinite(r.enter) && Number.isFinite(r.exit) && r.exit < r.enter
    && Number.isFinite(r.requiredS) && r.requiredS > 0 && Number.isFinite(r.decayPerS) && r.decayPerS >= 0;
}
/**
 * One authoritative sample of the opening. Dwell accumulates above `enter`,
 * decays inside the band, resets below `exit`; an ineligible sample, a
 * stalled interval or a bad number resets it outright. First acquisition
 * starts at zero — the unobserved prior interval is never credited.
 */
export function updateOpening(previous: Opening, score: number, elapsedS: number, eligible: boolean, rules: HoldRules): Opening {
  if (!eligible || !Number.isFinite(score) || !Number.isFinite(elapsedS) || elapsedS < 0) return { armed: false, heldS: 0 };
  if (elapsedS > OPENING_STALL_S) return { armed: false, heldS: 0 };
  const armed = previous.armed ? score >= rules.exit : score >= rules.enter;
  const heldS = !armed || !previous.armed ? 0
    : score >= rules.enter
      ? Math.min(rules.requiredS, previous.heldS + elapsedS)
      : Math.max(0, previous.heldS - rules.decayPerS * elapsedS);
  return { armed, heldS };
}
/** Commit needs BOTH the arm and the full dwell (plus the hard gates the tick re-checks). */
export function openingReady(o: Opening, rules: HoldRules): boolean {
  return o.armed && o.heldS >= rules.requiredS;
}

// ── Observed facts — what the hunter has actually SEEN (bit flags) ────────
export const FACT_INJURED = 1;   // the walk reads hurt (hp under INJURED_FRAC while in view)
export const FACT_COMBAT = 2;    // seen fighting / just hit by something else
export const FACT_UNARMED = 4;   // no weapon in the hands
export const FACT_STILL = 8;     // standing, absorbed in something
export const FACT_ALONE = 16;    // nobody within witness range
export const FACT_DARK = 32;     // night around the target
/** The MEANINGFUL-DISADVANTAGE gate: a witnessed wound, a fight, or empty
 *  hands. Solitude and darkness are context (they raise the score), never
 *  the gate — "alone at night" is not an observed weakness. */
export const DISADVANTAGE_MASK = FACT_INJURED | FACT_COMBAT | FACT_UNARMED;
export function hasDisadvantage(facts: number): boolean { return (facts & DISADVANTAGE_MASK) !== 0; }
/** Below this hp fraction the target visibly favours a side. */
export const INJURED_FRAC = 0.6;
/** Observed facts are trusted this long after the last clear sight. */
export const FACTS_FRESH_S = 20;

// ── The hunter's senses ───────────────────────────────────────────────────
/** How far it can pick the target out (m) — a better hunter looks further. */
export function visionRangeFor(t: BountyTier): number { return 100 + t.tier * 15; }
/** Belief confidence decays with the age of the last observation (τ s). */
export const BELIEF_TAU_S = 45;
export function beliefConfidence(c0: number, ageS: number): number { return c0 * Math.exp(-Math.max(0, ageS) / BELIEF_TAU_S); }
/** The search radius grows from the last observation (m); an inexpensive approximation, not a distribution. */
export const SEARCH_R0_M = 8;
export const SEARCH_GROW_MPS = 1.6;
export const SEARCH_RMAX_M = 90;
export function searchRadius(ageS: number): number { return Math.min(SEARCH_RMAX_M, SEARCH_R0_M + SEARCH_GROW_MPS * Math.max(0, ageS)); }
/** Predicted position from the last observation and its velocity, horizon-capped. */
export const PREDICT_HORIZON_S = 8;
export function predictPos(sx: number, sz: number, vx: number, vz: number, ageS: number): { x: number; z: number } {
  const t = Math.max(0, Math.min(PREDICT_HORIZON_S, ageS));
  return { x: sx + vx * t, z: sz + vz * t };
}

// ── Vantage candidates (the ring is a desired BAND, not a forced position) ─
export interface Candidate { x: number; z: number; bearing: number; r: number }
/** Evenly spread bearings around the target, radius jittered inside the tier
 *  band (0.85..1.15 × shadowRadiusM); `phase01` rotates the spread so two
 *  materializations never reuse the same twelve spots. Pure. */
export function ringCandidates(px: number, pz: number, t: BountyTier, n: number, phase01: number): Candidate[] {
  const out: Candidate[] = [];
  for (let i = 0; i < n; i++) {
    const bearing = ((i + phase01) / n) * Math.PI * 2;
    const jit = (((i * 7919 + 13) % 97) / 97);
    const r = t.shadowRadiusM * (0.85 + jit * 0.3);
    out.push({ x: px + Math.sin(bearing) * r, z: pz + Math.cos(bearing) * r, bearing, r });
  }
  return out;
}
/** THE FACING LAW: a three.js `rotation.y = yaw` body looks along (−sin yaw, −cos yaw),
 *  so the BEARING (atan2(dx, dz)) of what it faces is yaw + π — every "in front of / behind
 *  the target" test goes through this, never through the raw yaw. */
export function forwardBearing(yaw: number): number { return yaw + Math.PI; }
/** Is (x, z) inside the target's forward view cone (±halfAngle around its facing)? */
export function inViewCone(px: number, pz: number, yaw: number, x: number, z: number, halfAngle = 1.05): boolean {
  const b = Math.atan2(x - px, z - pz);
  let rel = b - forwardBearing(yaw);
  while (rel > Math.PI) rel -= Math.PI * 2;
  while (rel < -Math.PI) rel += Math.PI * 2;
  return Math.abs(rel) <= halfAngle;
}
export interface CandidateTerms {
  los: boolean;      // can it see the target from there (observation quality)
  cover: number;     // 0..1 — walls / trunks near enough to step behind
  travelM: number;   // route length from where it stands now
  exposed: boolean;  // inside the target's view cone with a clear line
  reuse: number;     // 0..1 — how recently this bearing was used
  escape: number;    // 0..1 — open ground behind it to leave through
  approach: number;  // 0..1 — sits on the target's predicted route
}
/** S = 2O + 2K + 1.5E + A − 2X − T − R, every term normalised. Invalid
 *  candidates are rejected BEFORE scoring — a wall is not a low score. */
export function scoreCandidate(c: CandidateTerms): number {
  const O = c.los ? 1 : 0.3;
  const T = Math.max(0, Math.min(1, c.travelM / 250));
  return 2 * O + 2 * c.cover + 1.5 * c.escape + c.approach - 2 * (c.exposed ? 1 : 0) - T - c.reuse;
}
/** A vantage never sits closer than this multiple of the flee radius. */
export const VANTAGE_MIN_MULT = 1.5;

// ── Evidence (what the TARGET can know) ───────────────────────────────────
export const EVIDENCE_AMBIENT = 0;
export const EVIDENCE_SUSPICIOUS = 1;
export const EVIDENCE_CORROBORATED = 2;
export const EVIDENCE_CONFIRMED = 3;
/** Sustained clear view needed to RECOGNISE a figure at a distance (s): about
 *  half a second up close, a second and a half at 150 m. */
export function recognitionNeededS(distM: number): number { return Math.max(0.4, Math.min(1.5, distM / 100)); }
/** Projected height of a standing body on screen (px). */
export function projectedHeightPx(distM: number, viewportH: number, vfovDeg: number, bodyM = 1.92): number {
  return (viewportH * bodyM) / (2 * Math.max(1, distM) * Math.tan((vfovDeg * Math.PI) / 360));
}
/** Below this many pixels a figure is a speck, not a silhouette. */
export const RECOGNIZE_MIN_PX = 10;
/** Two independent cues inside this window corroborate each other. */
export const CORROBORATE_WINDOW_S = 600;
/** Inside this range a recognised sighting IDENTIFIES the hunter outright. */
export const IDENTIFY_RANGE_M = 40;

// ── Cues — the evidence stream the server authors ─────────────────────────
export type CueKind = 'footstep' | 'silhouette' | 'radio' | 'witness' | 'birds' | 'door' | 'grit';
export const CUE_KINDS: CueKind[] = ['footstep', 'silhouette', 'radio', 'witness', 'birds', 'door', 'grit'];
/** How far a cue's reported position may sit from its true source (m) — a
 *  cue never leaks more accuracy than its channel supports. */
export const CUE_BLUR_M: Record<CueKind, number> = { footstep: 6, silhouette: 0, radio: 60, witness: 25, birds: 12, door: 10, grit: 3 };
export const CUE_GAP_MIN_S = 40;
export const CUE_GAP_MAX_S = 120;
export const CUE_WINDOW_S = 300;
export const CUE_BUDGET_PER_WINDOW = 4;   // a ceiling, not a quota
export const CUE_TTL_S = 90;
/** Eligible ACTIVE play before the first local cue may land (s). */
export const FIRST_CUE_MIN_S = 45;
export const FIRST_CUE_MAX_S = 150;
/** A cue must have been available this long before the first commit of an encounter. */
export const BUILDUP_MIN_S = 30;
export function cueGapS(h: number): number { return CUE_GAP_MIN_S + Math.max(0, Math.min(1, h)) * (CUE_GAP_MAX_S - CUE_GAP_MIN_S); }
export function firstCueDelayS(h: number): number { return FIRST_CUE_MIN_S + Math.max(0, Math.min(1, h)) * (FIRST_CUE_MAX_S - FIRST_CUE_MIN_S); }
/** Deterministic blur inside the kind's radius (h1, h2 in [0,1)). */
export function blurCue(kind: CueKind, x: number, z: number, h1: number, h2: number): { x: number; z: number } {
  const r = CUE_BLUR_M[kind] * Math.sqrt(Math.max(0, Math.min(1, h1)));
  const a = Math.max(0, Math.min(1, h2)) * Math.PI * 2;
  return { x: x + Math.sin(a) * r, z: z + Math.cos(a) * r };
}
/** May another cue land? (budget inside the window, and the gap since the last) */
export function cueAllowed(cueCountInWindow: number, sinceLastS: number, gapS: number): boolean {
  return cueCountInWindow < CUE_BUDGET_PER_WINDOW && sinceLastS >= gapS;
}

// ── Pacing (Left 4 Dead's lesson: build, peak, recover) ───────────────────
export const PRESSURE_TAU_S = 60;
export const PRESSURE_HIT = 0.25;      // the target took a blow (any source)
export const PRESSURE_COMMIT = 0.35;   // the hunter committed
export const PRESSURE_CUE = 0.05;      // a cue landed
export const PRESSURE_COMMIT_MAX = 0.6; // no new commit above this intensity
export function decayIntensity(i: number, dtS: number): number { return Math.max(0, Math.min(1, i)) * Math.exp(-Math.max(0, dtS) / PRESSURE_TAU_S); }
export function bumpIntensity(i: number, e: number): number { return Math.max(0, Math.min(1, i + e)); }
export const RECOVER_MIN_S = 90;
export const RECOVER_MAX_S = 240;
export const OPPORTUNITY_MIN_S = 45;
export const OPPORTUNITY_MAX_S = 150;
export const SEARCH_MIN_S = 20;
export const SEARCH_MAX_S = 60;
export const OBSERVE_MIN_S = 25;
export const OBSERVE_MAX_S = 45;
export const SHADOW_MAX_S = 120;
export const COMMIT_TELL_S = 1.2;
export function spanS(min: number, max: number, h: number): number { return min + Math.max(0, Math.min(1, h)) * (max - min); }

// ── Movement, contact, sanctuary ──────────────────────────────────────────
export const STALK_PACE_MPS = 2.6;   // the unhurried pace between vantages
export const HUNT_CHASE_MPS = 6.4;   // the run (mirrors the client's CHASE_SPEED)
export const LOST_DIST_M = 70;       // engage: further than this for LOST_S → contact broken
export const LOST_S = 12;
export const NO_LOS_S = 15;          // engage: no line of sight this long → contact broken
export const BLOCKED_S = 6;          // engage: a wall it cannot route around this long → contact broken
export const RETREAT_HP_FRAC = 0.2;  // it withdraws wounded (the damage is kept)
export const REENTRY_GRACE_S = 8;    // after sanctuary / shelter / reconnect: no commit, no blow
export const SHELTER_TTL_S = 12;     // a client shelter report is trusted this long
export const SHELTER_STANDOFF_M = 8; // it waits this far from the door
export const SHELTER_GIVEUP_S = 45;  // then it searches elsewhere
export const ACTIVE_STALE_S = 12;    // no movement update this long: not active play
/** Tactical memory is one bearing (where the target last broke contact) and a use count. */
export const MEMO_USES_MAX = 2;
